import { isGameId, type PresenceStatus } from "@bing/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { FriendView, GameInviteView } from "../api/client";
import { faDigits } from "../i18n/digits";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { pendingInviteHere } from "../store/session";
import { displayName } from "./profile-ui";
import { AvatarRing, Identity, Pill, SocialIcon } from "./social-ui";

/**
 * The friend-invite panel inside the private-table waiting room — shown to EVERY
 * seated player, not only the host (card-47, operator 2026-10-08: «everyone already
 * at the table can invite from their friends list»); the server never checked who
 * sends a table invite. Two entry points meet:
 *  - the Friends-screen flow (`inviteFriendToGame`) creates a table and leaves a
 *    `pendingInvite` marker BOUND to that create; the host's panel of THAT table
 *    auto-sends it the moment the roomId exists. No other panel ever does — not a
 *    guest's, and not the host's panel of any other table (a crown that arrived, a
 *    rematch successor, a table created later): a marker left by a refused create
 *    never fires (card-a5 item 15);
 *  - every accepted friend is listed with a direct «دعوت» button.
 * Each outgoing invite shows its live state: a countdown chip while PENDING
 * (60s TTL) with «لغو دعوت», then پیوست / رد کرد / منقضی شد — pushed live via
 * the presence socket (`invite_accepted` / `invite_declined` / `invite_expired`).
 *
 * BUILT FOR A LONG FRIEND LIST (the waiting room is a phone-sized bottom sheet):
 *  - only the first {@link INVITE_PAGE_SIZE} friends render; «بیشتر» appends the
 *    next page (pure client-side paging over the already-loaded `friends` — no
 *    extra API call), so a 300-friend account doesn't mount 300 rows;
 *  - past {@link INVITE_SEARCH_THRESHOLD} friends a search box appears and filters
 *    by the SHARED displayed name (`displayName` — nickname → Telegram name →
 *    handle) and by the `@appUsername`; typing resets back to the first page.
 */

/** Rows per page — one «بیشتر» tap appends another page of this many. */
export const INVITE_PAGE_SIZE = 8;
/** Beyond this many friends, scrolling is worse than typing → show the search box. */
export const INVITE_SEARCH_THRESHOLD = 15;

/** Screen-local Persian strings the shared `fa` dictionary doesn't cover yet
 * (mirrors `WaitingRoom` — we don't edit the central i18n file here). */
const t = {
  searchLabel: "جستجوی دوستان",
  searchPlaceholder: "اسم دوستت رو بنویس…",
  noMatches: "دوستی با این نام پیدا نشد",
  more: "بیشتر",
} as const;

/** Does `friend` match the (already normalized) search needle? Name first — the
 *  SHARED display-name resolution, so what the user reads is what they search —
 *  then the app handle (typed with or without the leading `@`). */
function matchesQuery(friend: FriendView, needle: string): boolean {
  if (!needle) return true;
  if (displayName(friend).toLowerCase().includes(needle)) return true;
  return (friend.appUsername ?? "").toLowerCase().includes(needle);
}

export function InviteFriendList({
  gameId,
  roomId,
  seatedUserIds,
  consumePendingInvite,
}: {
  gameId: string;
  roomId: string;
  /**
   * CARD-98 ITEM 1 (operator, 2026-10-27) — the app ids of the humans seated at this table NOW.
   * «پیوست» is said only while that friend is one of them; a friend who left gets the invite button
   * again (the game-server no longer bars a guest from a private table they left). Required, so every
   * mount has to pass the roster: an ACCEPTED invite alone cannot say whether they are still here.
   */
  seatedUserIds: readonly string[];
  /**
   * The ROLE half of the auto-send gate for the Friends-screen pick: true only on the HOST's panel.
   * It is not the whole gate — hosting a table is not having created it for the pick (a guest
   * crowned before the start, an owner following their rematch): the pick's own binding,
   * `pendingInviteHere`, says whether THIS is its table (card-a5 item 15). Required, so every
   * mount has to decide.
   */
  consumePendingInvite: boolean;
}): ReactNode {
  const friends = useSession((s) => s.friends);
  const presence = useSession((s) => s.presence);
  const outgoingInvites = useSession((s) => s.outgoingInvites);
  const pick = useSession(pendingInviteHere);
  const clearPendingInvite = useSession((s) => s.clearPendingInvite);
  const sendGameInvite = useSession((s) => s.sendGameInvite);
  const cancelGameInvite = useSession((s) => s.cancelGameInvite);
  const refreshFriends = useSession((s) => s.refreshFriends);
  const refreshInvites = useSession((s) => s.refreshInvites);

  // card-98 item 1 — WHO HAS BEEN SEATED HERE: «دعوت دوباره» is for a friend who LEFT (seen seated,
  // then gone); one who accepted and is still on their way in keeps «پیوست». Per table.
  const seenSeated = useRef<{ roomId: string; ids: Set<string> }>({ roomId, ids: new Set() });
  if (seenSeated.current.roomId !== roomId) seenSeated.current = { roomId, ids: new Set() };
  for (const id of seatedUserIds) seenSeated.current.ids.add(id);

  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /** How many pages are revealed (client-side paging over the loaded friends). */
  const [pages, setPages] = useState(1);

  useEffect(() => {
    void refreshFriends();
    void refreshInvites();
  }, [refreshFriends, refreshInvites]);

  const withBusy = useCallback(async (key: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy((prev) => new Set(prev).add(key));
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(faApiError(err));
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const send = useCallback(
    (toUserId: string): Promise<void> =>
      withBusy(toUserId, async () => {
        if (!isGameId(gameId) || !roomId) return;
        await sendGameInvite(toUserId, gameId, roomId);
      }),
    [withBusy, sendGameInvite, gameId, roomId],
  );

  // The friend picked back on the Friends screen: auto-invite once the room
  // reports its id (consume the marker exactly once) — on the host's panel of the
  // table that pick created, and nowhere else.
  useEffect(() => {
    if (!consumePendingInvite || pick === null || !roomId) return;
    clearPendingInvite();
    void send(pick);
  }, [consumePendingInvite, pick, roomId, clearPendingInvite, send]);

  // The `@` is optional when searching by handle; matching is case-insensitive.
  const needle = query.trim().replace(/^@/, "").toLowerCase();
  const matches = useMemo(() => friends.filter((f) => matchesQuery(f, needle)), [friends, needle]);

  if (friends.length === 0) return null; // the share link above covers strangers

  const searchable = friends.length > INVITE_SEARCH_THRESHOLD;
  const visible = matches.slice(0, pages * INVITE_PAGE_SIZE);
  const remaining = matches.length - visible.length;

  /** The NEWEST invite I sent this friend FOR THIS TABLE. Pick by `createdAt` explicitly rather
   *  than "first match": after a cancel + re-invite the store holds BOTH the old terminal row and
   *  the fresh one (and merged lists aren't reliably ordered), so a status-blind `find` could show a
   *  stale «لغو شد»/«پیوست» chip instead of the live invite's state. (ISO strings sort chronologically.) */
  const inviteFor = (userId: string): GameInviteView | undefined =>
    outgoingInvites
      .filter((i) => i.roomId === roomId && i.to.userId === userId)
      .reduce<GameInviteView | undefined>(
        (newest, i) => (!newest || i.createdAt > newest.createdAt ? i : newest),
        undefined,
      );

  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 flex items-center gap-1.5 font-caption text-caption text-on-surface-variant">
        <SocialIcon name="group" className="h-4 w-4 text-secondary" />
        <span>{fa.invite.inviteFriendsTitle}</span>
        <span className="text-on-surface-variant/70" style={{ fontVariantNumeric: "tabular-nums" }}>
          {faDigits(friends.length)} {fa.friends.count}
        </span>
      </p>

      {searchable ? (
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-on-surface-variant">
            <SocialIcon name="search" className="h-4 w-4" />
          </span>
          <input
            type="text"
            dir="rtl"
            aria-label={t.searchLabel}
            placeholder={t.searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPages(1); // a new filter always starts at the first page
            }}
            className="w-full appearance-none rounded-lg border border-outline-variant bg-surface-container-low py-1.5 pl-2 pr-8 text-right font-caption text-caption text-on-surface outline-none transition-colors placeholder:text-on-surface-variant focus:border-secondary"
          />
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="m-0 font-caption text-caption text-error">
          {error}
        </p>
      ) : null}

      {/* The one scroll area of the sheet: friends as a single-column list, ONE row each (avatar +
          name + action), sitting close together. The height shows ~3 rows and the rest scrolls,
          keeping the felt + seats above their room no matter how many friends are revealed. */}
      <div className="max-h-[124px] overflow-y-auto overflow-x-hidden overscroll-contain">
        {visible.length === 0 ? (
          <p className="m-0 py-2 text-center font-caption text-caption text-on-surface-variant">
            {t.noMatches}
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {visible.map((f) => (
              <FriendInviteRow
                key={f.userId}
                friend={f}
                presence={presence[f.userId]}
                invite={inviteFor(f.userId)}
                left={!seatedUserIds.includes(f.userId) && seenSeated.current.ids.has(f.userId)}
                busy={busy.has(f.userId)}
                canSend={!!roomId}
                onSend={() => void send(f.userId)}
                onCancel={(inviteId) => void withBusy(f.userId, () => cancelGameInvite(inviteId))}
              />
            ))}
          </div>
        )}
        {remaining > 0 ? (
          <button
            type="button"
            onClick={() => setPages((p) => p + 1)}
            className="mt-1.5 flex w-full flex-none cursor-pointer items-center justify-center gap-1 rounded-lg border border-outline-variant bg-surface-container-low py-1.5 font-label-md text-label-md text-secondary transition-colors hover:bg-surface-bright active:scale-95"
          >
            {t.more}
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              ({faDigits(remaining)} {fa.friends.count})
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function FriendInviteRow({
  friend,
  presence,
  invite,
  left,
  busy,
  canSend,
  onSend,
  onCancel,
}: {
  friend: FriendView;
  presence?: PresenceStatus;
  invite: GameInviteView | undefined;
  /** card-98 item 1 — seated at this table once, and not any more. */
  left: boolean;
  busy: boolean;
  canSend: boolean;
  onSend: () => void;
  onCancel: (inviteId: string) => void;
}): ReactNode {
  // ONE compact ROW: [avatar] [name] [action(s)] all on a single line, full width.
  //  - the avatar is fixed-size (never shrinks);
  //  - the NAME takes the slack (`flex-1 min-w-0`) and `truncate`s — bounded to the space left
  //    after the avatar + action, so a long name ellipsizes instead of pushing anything out;
  //  - the ACTION side is `flex-none` (reserves its width first) and NEVER wraps, so even the
  //    widest terminal state («منقضی شد» chip + «دعوت دوباره») stays inside the box, no overlap.
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-outline-variant/60 bg-surface-container-low px-2 py-1">
      <AvatarRing
        avatarId={friend.avatarId}
        avatarPhotoId={friend.avatarPhotoId}
        size={24}
        status={presence}
      />
      <div className="min-w-0 flex-1">
        <Identity user={friend} />
      </div>
      <div className="flex flex-none items-center gap-1">
        <InviteAction
          invite={invite}
          left={left}
          busy={busy}
          canSend={canSend}
          onSend={onSend}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
}

/** The trailing control per invite state (none/CANCELED → invite; PENDING →
 * countdown + cancel; terminal → status chip, re-invitable when it went dead). */
function InviteAction({
  invite,
  left,
  busy,
  canSend,
  onSend,
  onCancel,
}: {
  invite: GameInviteView | undefined;
  left: boolean;
  busy: boolean;
  canSend: boolean;
  onSend: () => void;
  onCancel: (inviteId: string) => void;
}): ReactNode {
  // card-98 item 1 — an accepted friend who has LEFT the table is invitable again.
  if (!invite || invite.status === "CANCELED" || (invite.status === "ACCEPTED" && left)) {
    return (
      <Pill
        variant="primary"
        icon="invite"
        label={invite ? fa.invite.reinvite : fa.invite.invite}
        disabled={busy || !canSend}
        onClick={onSend}
      />
    );
  }
  if (invite.status === "PENDING") {
    // NO countdown timer in the waiting room (it overflowed the compact grid cell and the inviter
    // doesn't need it — the invite still expires server-side after its 60s TTL). Just the «لغو»
    // action: its presence IS the "pending" state; canceling drops the friend's invite live.
    return (
      <Pill
        variant="outline"
        icon="close"
        label={fa.invite.cancel}
        disabled={busy}
        onClick={() => onCancel(invite.id)}
      />
    );
  }
  if (invite.status === "ACCEPTED") {
    return <StatusChip tone="win" label={fa.invite.accepted} />;
  }
  // DECLINED / EXPIRED — show what happened and allow another try.
  return (
    <>
      <StatusChip
        tone="muted"
        label={invite.status === "DECLINED" ? fa.invite.declined : fa.invite.expired}
      />
      <Pill
        variant="outline"
        icon="invite"
        label={fa.invite.reinvite}
        disabled={busy || !canSend}
        onClick={onSend}
      />
    </>
  );
}

function StatusChip({ tone, label }: { tone: "win" | "muted"; label: string }): ReactNode {
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-1 font-label-md text-label-md ${
        tone === "win"
          ? "border-success-win/50 text-success-win"
          : "border-outline-variant/60 text-on-surface-variant"
      }`}
    >
      {label}
    </span>
  );
}
